import React, { useState, useEffect } from 'react';
import useWhichReverseGeocodeService from 'emergency_data/src/hooks/useWhichReverseGeocodeService';
import { googleReverseGeocode } from '@rsos/components/capstone/common/Map/utils';
import { ESRI } from '@rsos/maps/constants/reverseGeocodeServices';
import gisReverseGeocodeLocation from '@rsos/maps/interface/reverseGeocode/gisReverseGeocodeLocation';
import { ESRI_GEOCODE_SERVICE_URL } from '@rsos/maps/interface/reverseGeocode/helpers';
import LocationSummary from './LocationSummary';

const find = (components, key, nameType = 'long_name') => {
  const result = components[key] || {};
  return result[nameType] || '';
};

const flattenComponents = components => {
  const o = {};
  components.forEach(part => {
    part.types.forEach(type => {
      o[type] = part;
    });
  });
  return o;
};

const normalizeToLocation = result => {
  const components = flattenComponents(result.address_components);
  const street_number = find(components, 'street_number');
  const street_name = find(components, 'route');
  let street_address = '';
  if (street_number && street_name) {
    street_address = `${street_number} ${street_name}`;
  }

  const location_name = find(components, 'point_of_interest');

  const region = find(components, 'administrative_area_level_1', 'short_name');

  let locality = find(components, 'locality');
  if (!locality) {
    locality = find(components, 'sublocality');
  }
  const postal_code = find(components, 'postal_code');
  const country_code = find(components, 'country');

  const location = {
    location_name,
    street_address,
    locality,
    region,
    postal_code,
    country_code,
  };

  return location;
};

const esriReverseGeocode = latlng => {
  const location = {
    latitude: latlng.lat,
    longitude: latlng.lng,
    onReverseGeocoding: () => {},
    serviceUrl: ESRI_GEOCODE_SERVICE_URL,
  };
  return gisReverseGeocodeLocation(location);
};

const createReverseGeocode = service => {
  const fn = service === ESRI ? esriReverseGeocode : googleReverseGeocode;
  return latlng => {
    return new Promise(async (resolve, reject) => {
      try {
        const results = await fn(latlng);

        if (service === ESRI) {
          const normalized = {
            location_name: results.PlaceName || '',
            street_address: results.Address,
            locality: results.City,
            region: results.Region,
            postal_code: results.Postal,
            country_code: results.CountryCode,
          };
          resolve(normalized);
        } else {
          let result;
          if (Array.isArray(results) && results.length > 0) {
            result = results[0];
          }

          if (result) {
            const normalized = normalizeToLocation(result);
            if (normalized) {
              resolve(normalized);
            }
          }
        }
      } catch (error) {
        reject(error);
      }
    });
  };
};

const Location = ({
  location: locationProp,
  overridden,
  coordinates = null,
}) => {
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [location, setLocation] = useState(locationProp);
  const [service, setService] = useState('');

  const serviceName = useWhichReverseGeocodeService();
  useEffect(() => {
    if (serviceName) {
      setService(serviceName);
    }
  }, [serviceName]);

  useEffect(() => {
    setLocation(locationProp);

    if (!locationProp && coordinates && service) {
      const latlng = {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      };

      const lookup = async () => {
        setIsGeocoding(true);
        try {
          const doReverseGeocode = createReverseGeocode(service);
          const results = await doReverseGeocode(latlng);
          if (results) {
            setLocation(results);
          }
          setIsGeocoding(false);
        } catch (error) {
          setLocation(undefined);
        }
      };
      if (!isGeocoding) {
        lookup();
      }
    }
    return () => setLocation(undefined);
  }, [locationProp, coordinates, service]);

  return (
    <LocationSummary
      isLoading={isGeocoding}
      location={location}
      overridden={overridden}
      coordinates={coordinates}
    />
  );
};

export default Location;
